import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { db } from '../utils/firebase';
import { TimberKit } from '../types';
import { AppHeader } from '../components/ui/AppHeader';
import { Plus, Package, Pencil, Copy, Trash2, Tag, Grid, List, Search, Filter } from 'lucide-react';
import './KitManagementPage.css';

type ViewMode = 'grid' | 'list';
type FilterStatus = 'all' | 'active' | 'draft' | 'archived';

const KitManagementPage: React.FC = () => {
    const navigate = useNavigate();
    const [kits, setKits] = useState<TimberKit[]>([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState<ViewMode>('grid');
    const [filterStatus, setFilterStatus] = useState<FilterStatus>('all');
    const [searchQuery, setSearchQuery] = useState('');

    // Load kits from Firestore
    useEffect(() => {
        const kitsRef = collection(db, 'timber_kits');
        const q = query(kitsRef, orderBy('updatedAt', 'desc'));

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const kitData: TimberKit[] = [];
            snapshot.forEach((doc) => {
                const data = doc.data();
                kitData.push({
                    ...data,
                    id: doc.id,
                    createdAt: data.createdAt?.toDate?.() || new Date(),
                    updatedAt: data.updatedAt?.toDate?.() || new Date(),
                    saleEndsAt: data.saleEndsAt?.toDate?.() || undefined,
                } as TimberKit);
            });
            setKits(kitData);
            setLoading(false);
        });

        return () => unsubscribe();
    }, []);

    // Filter kits
    const filteredKits = kits.filter((kit) => {
        // Status filter
        if (filterStatus !== 'all' && kit.status !== filterStatus) {
            return false;
        }
        // Search filter
        if (searchQuery) {
            const query = searchQuery.toLowerCase();
            return (
                kit.name.toLowerCase().includes(query) ||
                kit.description?.toLowerCase().includes(query)
            );
        }
        return true;
    });

    const handleCreateKit = () => {
        navigate('/kit-builder');
    };

    const handleEditKit = (kitId: string) => {
        navigate(`/kit-builder/${kitId}`);
    };

    const handleDuplicateKit = async (kit: TimberKit) => {
        // TODO: Implement duplicate functionality
        console.log('Duplicate kit:', kit.id);
    };

    const handleArchiveKit = async (kitId: string) => {
        if (!window.confirm('Are you sure you want to archive this kit?')) return;
        const kitRef = doc(db, 'timber_kits', kitId);
        await updateDoc(kitRef, { status: 'archived', updatedAt: new Date() });
    };

    const handleDeleteKit = async (kitId: string) => {
        if (!window.confirm('Are you sure you want to permanently delete this kit?')) return;
        const kitRef = doc(db, 'timber_kits', kitId);
        await deleteDoc(kitRef);
    };

    const formatCurrency = (value: number) => {
        return new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
            minimumFractionDigits: 0,
            maximumFractionDigits: 0,
        }).format(value);
    };

    const formatDate = (date: Date) => {
        return new Intl.DateTimeFormat('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
        }).format(date);
    };

    // Get price range from species pricing
    const getPriceRange = (kit: TimberKit) => {
        if (!kit.speciesPricing || kit.speciesPricing.length === 0) {
            return formatCurrency(kit.billableTotal);
        }
        const prices = kit.speciesPricing.map((sp) => sp.billableTotal);
        const min = Math.min(...prices);
        const max = Math.max(...prices);
        if (min === max) {
            return formatCurrency(min);
        }
        return `${formatCurrency(min)} - ${formatCurrency(max)}`;
    };

    // Check if sale is active
    const isSaleActive = (kit: TimberKit) => {
        if (!kit.isOnSale) return false;
        if (kit.saleEndsAt && new Date() > kit.saleEndsAt) return false;
        return true;
    };

    return (
        <div className="kit-management-page">
            <AppHeader />

            <div className="kit-management-content">
                <div className="kit-management-header">
                    <div className="header-left">
                        <h1>
                            <Package size={28} />
                            Kit Management
                        </h1>
                        <p className="header-subtitle">
                            Pre-built timber frame kits for quick quoting
                        </p>
                    </div>

                    <button className="create-kit-btn" onClick={handleCreateKit}>
                        <Plus size={20} />
                        Create New Kit
                    </button>
                </div>

                <div className="kit-management-toolbar">
                    <div className="toolbar-left">
                        <div className="search-box">
                            <Search size={18} />
                            <input
                                type="text"
                                placeholder="Search kits..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                        </div>

                        <div className="filter-dropdown">
                            <Filter size={18} />
                            <select
                                value={filterStatus}
                                onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
                            >
                                <option value="all">All Status</option>
                                <option value="active">Active</option>
                                <option value="draft">Draft</option>
                                <option value="archived">Archived</option>
                            </select>
                        </div>
                    </div>

                    <div className="toolbar-right">
                        <div className="view-toggle">
                            <button
                                className={viewMode === 'grid' ? 'active' : ''}
                                onClick={() => setViewMode('grid')}
                                title="Grid view"
                            >
                                <Grid size={18} />
                            </button>
                            <button
                                className={viewMode === 'list' ? 'active' : ''}
                                onClick={() => setViewMode('list')}
                                title="List view"
                            >
                                <List size={18} />
                            </button>
                        </div>
                    </div>
                </div>

                {loading ? (
                    <div className="kit-loading">
                        <div className="loading-spinner" />
                        <p>Loading kits...</p>
                    </div>
                ) : filteredKits.length === 0 ? (
                    <div className="kit-empty-state">
                        <Package size={64} strokeWidth={1} />
                        <h2>No kits found</h2>
                        <p>
                            {searchQuery || filterStatus !== 'all'
                                ? 'Try adjusting your search or filters'
                                : 'Create your first kit to get started'}
                        </p>
                        {!searchQuery && filterStatus === 'all' && (
                            <button className="create-first-btn" onClick={handleCreateKit}>
                                <Plus size={20} />
                                Create Your First Kit
                            </button>
                        )}
                    </div>
                ) : (
                    <div className={`kit-grid ${viewMode}`}>
                        {filteredKits.map((kit) => (
                            <div
                                key={kit.id}
                                className={`kit-card ${kit.status} ${isSaleActive(kit) ? 'on-sale' : ''}`}
                                onClick={() => handleEditKit(kit.id)}
                            >
                                {/* Thumbnail */}
                                <div className="kit-thumbnail">
                                    {kit.thumbnailUrl ? (
                                        <img src={kit.thumbnailUrl} alt={kit.name} />
                                    ) : (
                                        <div className="kit-thumbnail-placeholder">
                                            <Package size={48} strokeWidth={1} />
                                        </div>
                                    )}

                                    {/* Sale badge */}
                                    {isSaleActive(kit) && (
                                        <div className="sale-badge">
                                            <Tag size={14} />
                                            {kit.saleDiscountType === 'percentage'
                                                ? `${kit.saleDiscount}% OFF`
                                                : `$${kit.saleDiscount} OFF`}
                                            {kit.saleEndsAt && (
                                                <span className="sale-ends">
                                                    ends {new Date(kit.saleEndsAt).toLocaleDateString()}
                                                </span>
                                            )}
                                        </div>
                                    )}

                                    {/* Status badge */}
                                    <div className={`status-badge ${kit.status}`}>
                                        {kit.status}
                                    </div>
                                </div>

                                {/* Kit info */}
                                <div className="kit-info">
                                    <h3 className="kit-name">{kit.name}</h3>
                                    {kit.description && (
                                        <p className="kit-description">{kit.description}</p>
                                    )}

                                    <div className="kit-meta">
                                        <span className="kit-updated">
                                            Updated {formatDate(kit.updatedAt)}
                                        </span>
                                    </div>
                                </div>

                                {/* Actions */}
                                <div className="kit-actions" onClick={(e) => e.stopPropagation()}>
                                    <button
                                        className="action-btn edit"
                                        onClick={() => handleEditKit(kit.id)}
                                        title="Edit"
                                    >
                                        <Pencil size={16} />
                                    </button>
                                    <button
                                        className="action-btn duplicate"
                                        onClick={() => handleDuplicateKit(kit)}
                                        title="Duplicate"
                                    >
                                        <Copy size={16} />
                                    </button>
                                    {kit.status !== 'archived' ? (
                                        <button
                                            className="action-btn archive"
                                            onClick={() => handleArchiveKit(kit.id)}
                                            title="Archive"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    ) : (
                                        <button
                                            className="action-btn delete"
                                            onClick={() => handleDeleteKit(kit.id)}
                                            title="Delete permanently"
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};

export default KitManagementPage;
